import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Sheet,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  AspectRatio,
  List,
  ListItem,
} from '@mui/joy';
import { Download } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';

const IMAGES_PER_PAGE = 10;
const anniDisponibili = [2026, 2025, 2024, 2023];

function extractTimestamp(filename) {
  const match = filename.match(/(\d{13})/);
  return match ? Number(match[1]) : null;
}

function getYearMonthLabel(timestamp) {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = date.toLocaleString('it-IT', { month: 'long' });
  return {
    year,
    month: month.charAt(0).toUpperCase() + month.slice(1),
    label: `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`,
  };
}

export default function UploadList() {
  const [uploadsByMonth, setUploadsByMonth] = useState({});
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(2025);
  const [selectedMonthLabel, setSelectedMonthLabel] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const selectedOrg = localStorage.getItem('selectedOrg');

  useEffect(() => {
    const fetchUploads = async () => {
      setLoading(true);
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/checklist/protected/uploads/${selectedOrg}/${year}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const text = await res.text();
        if (!res.ok) {
          setUploadsByMonth({});
          return;
        }

        const data = JSON.parse(text);
        if (Array.isArray(data)) {
          const sorted = [...data].sort().reverse();
          const grouped = {};

          sorted.forEach((file) => {
            const ts = extractTimestamp(file);
            if (ts) {
              const { label, year, month } = getYearMonthLabel(ts);
              if (!grouped[label]) grouped[label] = { year, month, files: [] };
              grouped[label].files.push(file);
            }
          });

          setUploadsByMonth(grouped);

          const firstLabel = Object.keys(grouped).find(key => grouped[key].year === year);
          if (firstLabel) setSelectedMonthLabel(firstLabel);

          setPage(1);
        } else {
          setUploadsByMonth({});
        }
      } catch (err) {
        console.error('Errore fetch:', err);
        setUploadsByMonth({});
      } finally {
        setLoading(false);
      }
    };

    fetchUploads();
  }, [selectedOrg, year]);

  const currentFiles = useMemo(() => {
    return selectedMonthLabel && uploadsByMonth[selectedMonthLabel]
      ? uploadsByMonth[selectedMonthLabel].files
      : [];
  }, [selectedMonthLabel, uploadsByMonth]);

  const paginated = useMemo(() => {
    const start = (page - 1) * IMAGES_PER_PAGE;
    return currentFiles.slice(start, start + IMAGES_PER_PAGE);
  }, [currentFiles, page]);

  const totalPages = Math.ceil(currentFiles.length / IMAGES_PER_PAGE);

  return (
    <Box>
      <Typography level="h3" mb={2}>Archivio Immagini Checklist</Typography>

      <Box display="flex" gap={4}>
        {/* Sidebar sinistra */}
        <Box sx={{ minWidth: '180px' }}>
          {anniDisponibili.map((y) => {
            const mesiAnno = Object.entries(uploadsByMonth)
              .filter(([label, value]) => value.year === y);

            return (
              <Box key={y} mb={3}>
                <Button
                  size="sm"
                  fullWidth
                  variant={y === year ? 'solid' : 'outlined'}
                  onClick={() => {
                    setYear(y);
                    const primoMese = mesiAnno[0]?.[0];
                    if (primoMese) setSelectedMonthLabel(primoMese);
                  }}
                  sx={{ mb: 1 }}
                >
                  {y}
                </Button>

                <List size="sm">
                  {mesiAnno.map(([label, { month }]) => (
                    <ListItem key={label} sx={{ py: 0.25, px: 0 }}>
                      <Button
                        size="sm"
                        fullWidth
                        variant={label === selectedMonthLabel ? 'solid' : 'soft'}
                        onClick={() => {
                          setYear(y);
                          setSelectedMonthLabel(label);
                          setPage(1);
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          textTransform: 'capitalize',
                          minHeight: '28px',
                          px: 1,
                        }}
                      >
                        {month}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            );
          })}
        </Box>

        {/* Contenuto immagini */}
        <Box flex={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          ) : paginated.length > 0 ? (
            <>
              <Box
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
                gap={2}
              >
                {paginated.map((file, idx) => {
                  const imageUrl = `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/checklist/protected/uploads/${selectedOrg}/${year}/${file}`;

                  return (
                    <Sheet
                      key={idx}
                      variant="outlined"
                      sx={{
                        borderRadius: 'md',
                        p: 0.5,
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover .download-icon': {
                          opacity: 1,
                        },
                      }}
                    >
                      <IconButton
                        component="a"
                        href={imageUrl}
                        download
                        size="sm"
                        color="neutral"
                        variant="solid"
                        className="download-icon"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 2,
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          },
                        }}
                      >
                        <Download size={16} />
                      </IconButton>

                      <AspectRatio
                        ratio="1"
                        sx={{
                          borderRadius: 'sm',
                          cursor: 'pointer',
                          width: '100%',
                        }}
                        onClick={() => setSelectedImage(imageUrl)}
                      >
                        <img src={imageUrl} alt={`upload-${idx}`} loading="lazy" />
                      </AspectRatio>
                    </Sheet>
                  );
                })}
              </Box>

              {totalPages > 1 && (
                <Box mt={3} display="flex" justifyContent="center" gap={2}>
                  <Button
                    variant="outlined"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Precedente
                  </Button>
                  <Typography level="body-sm">Pagina {page} di {totalPages}</Typography>
                  <Button
                    variant="outlined"
                    size="sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Successiva
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography level="body-md" color="neutral" mt={2}>
              Nessuna immagine caricata per il periodo selezionato.
            </Typography>
          )}
        </Box>
      </Box>

      <Modal open={!!selectedImage} onClose={() => setSelectedImage(null)}>
        <ModalDialog layout="center" size="lg">
          <ModalClose />
          <Typography level="title-lg" mb={1}>Anteprima</Typography>
          <Box display="flex" justifyContent="center" mb={2}>
            <img
              src={selectedImage}
              alt="Anteprima"
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }}
            />
          </Box>
          <Button
            component="a"
            href={selectedImage}
            download
            fullWidth
            startDecorator={<Download size={16} />}
          >
            Scarica
          </Button>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
